import type { IAugmentedJQuery, IComponentController, IOnChangesObject } from 'angular';
import template from './widget-configuration-panel.component.html';
import { DashboardWidgetDTO, WidgetInstance, DashboardService } from '../dashboard.service';
import { DashboardPageComponent, DashboardPageController } from '../dashboard-page/dashboard-page.component';
import type { NotificationService } from '../../App/services/mx-notification-service';
import type { I18n } from '../../i18n/mx-internationalization';
import type { LocalizationModeHelper } from '../../Shell/PandoraSolutionBuilderAgent/services/dialog-localization-mode-helper';
import type { DashboardLocalizationService } from '../dashboard-localization.service';

const formSelector = 'mx-widget-configuration-panel';

export class WidgetConfigurationPanelController implements IComponentController {
	static readonly $inject = [
		'$element',
		'mx.internationalization',
		'mx.shell.NotificationService',
		'mx.sb.Dashboard.Service',
		'mx.sb.Dashboard.DashboardLocalizationService',
		'mx.SolutionBuilderAgent.mxLocalizationModeHelper',
	];
	public dashboardsList: { icon?: string; label: string; onItemClick: () => void }[] = [];
	public extraWidgetInstanceActions: { icon?: string; label: string; onItemClick: () => void }[] = [];
	public serviceContextInfo?: { widgetId: string; viewName: string };

	widgetInstance!: WidgetInstance;
	templateData!: WidgetInstance['templateData'];
	page!: DashboardPageController;
	private isLocalizationModeEnabled = false;

	constructor(
		private readonly $element: IAugmentedJQuery,
		private readonly i18n: I18n,
		private readonly notificationService: NotificationService,
		private readonly dashboardService: DashboardService,
		private readonly dashboardLocalizationService: DashboardLocalizationService,
		private readonly localizationModeHelper: ReturnType<typeof LocalizationModeHelper>
	) {}

	async $onInit() {
		this.dashboardsList = this.getAvailableDashboardsList();
		this.extraWidgetInstanceActions = [this.getLocalizationAction()];
		this.setServiceContext(this.widgetInstance);
	}

	$onChanges(onChangesObj: IOnChangesObject) {
		if (onChangesObj.widgetInstance || onChangesObj.templateData) {
			this.setServiceContext(this.widgetInstance);
		}
	}

	private setServiceContext(widgetInstance: WidgetInstance) {
		this.serviceContextInfo = {
			widgetId: widgetInstance.widgetId ?? '',
			viewName: widgetInstance.templateData?.configurationViewKey ?? '',
		};
	}

	$onDestroy() {
		if (this.isLocalizationModeEnabled) {
			this.disableLocalizationMode();
		}
	}

	private getLocalizationAction() {
		const localizationAction = {
			icon: 'translate',
			label: this.i18n.get('shell.common.enable-localization-mode'),
			onItemClick: () => {
				this.isLocalizationModeEnabled = !this.isLocalizationModeEnabled;
				localizationAction.label = this.i18n.get(this.isLocalizationModeEnabled ? 'shell.common.disable-localization-mode' : 'shell.common.enable-localization-mode');

				if (this.isLocalizationModeEnabled) {
					this.enableLocalizationMode();
				} else {
					this.disableLocalizationMode();
				}
			},
		};
		return localizationAction;
	}

	private enableLocalizationMode() {
		this.localizationModeHelper.enableMode(this.$element);

		this.localizationModeHelper.applyStylesForControls(this.$element, this.widgetInstance.localization.fields, formSelector);

		this.$element[0].addEventListener('click', this.localizationControlClickHandler, true);
	}

	private disableLocalizationMode() {
		this.localizationModeHelper.disableMode(this.$element);
		this.localizationModeHelper.removeStylesForControls();
		this.$element[0].removeEventListener('click', this.localizationControlClickHandler, true);
	}

	localizationControlClickHandler = async (event: Event) => {
		const currentTarget = event.currentTarget;
		await this.dashboardLocalizationService.loadTranslations(this.widgetInstance);
		const controlElm = this.localizationModeHelper.preventUsualControlHandler(event, this.widgetInstance.localization.fields, '', currentTarget);
		if (controlElm) {
			const modelInfo = this.localizationModeHelper.getModelInfoFromElement(controlElm);
			const dataModelHelper = this.localizationModeHelper.getDataModelGetterSetter(modelInfo, this.dataModel['WidgetConfiguration']);

			const currentValue = dataModelHelper.get();
			const { localizedField, currentLang } = await this.localizationModeHelper.openLocalizationDialog(
				controlElm,
				this.widgetInstance.localization.current[modelInfo.translationKey],
				this.widgetInstance.localization.translations,
				currentValue
			);

			this.localizationModeHelper.applyTranslations(localizedField, modelInfo.translationKey, this.widgetInstance.localization.translations);

			dataModelHelper.set(this.localizationModeHelper.getValueWithFallback(localizedField, currentLang));
		}
		return false;
	};

	public get dataModel() {
		return this.widgetInstance?.dataModel;
	}

	async setWidget(widget: DashboardWidgetDTO) {
		await this.page.applyWidget(widget);
		this.setServiceContext(this.widgetInstance);
	}

	async onBackClick() {
		if (this.page.configurableWidget?.isNew) {
			this.page.backToCategories();
		} else {
			await this.page.cancelEditing();
		}
	}

	async onSave() {
		try {
			await this.page.finishEditing();
			this.notificationService.success(this.i18n.get('shell.dashboards.widgetSavedMsg'));
		} catch (e) {
			this.notificationService.error(this.i18n.get('shell.dashboards.widgetNotSavedMsg'));
			console.error(e);
		}
	}

	private getAvailableDashboardsList() {
		return this.dashboardService
			.getDashboardNavigationItems()
			.map((item) => ({
				icon: item.icon,
				label: item.title,
				onItemClick: async () => {
					await this.saveWidgetInstanceAsNew(item.id);
				},
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}

	async saveWidgetInstanceAsNew(dashboardNavigationId: string) {
		try {
			await this.page.saveWidgetInstanceAsNew(dashboardNavigationId);

			this.notificationService.success(this.i18n.get('shell.dashboards.widgetSavedMsg'));
		} catch (e) {
			this.notificationService.error(this.i18n.get('shell.dashboards.widgetNotSavedMsg'));
			console.error(e);
		}
	}
}

export const WidgetConfigurationPanelComponent = {
	name: 'mxWidgetConfigurationPanel',
	controller: WidgetConfigurationPanelController,
	controllerAs: 'vm',
	template,
	bindings: {
		widgetInstance: '<',
		templateData: '<',
	},
	require: {
		page: `^${DashboardPageComponent.name}`,
	},
};
